/**
 * DeleteAccountModal — веб-версия как в mobile
 * 3 шага: причина → предупреждение → ввод фразы подтверждения
 * Ключи: deleteAccount.*
 */

import React, { useState, useRef, useEffect } from 'react';
import { Modal } from '../Modal/Modal';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { useTranslation } from 'react-i18next';
import './DeleteAccountModal.css';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason?: string) => Promise<void>;
}

const DELETION_REASONS: { id: string; icon: string }[] = [
  { id: 'not_using', icon: 'clock' },
  { id: 'switched_app', icon: 'swap-horizontal' },
  { id: 'privacy', icon: 'shield' },
  { id: 'missing_features', icon: 'lightbulb' },
  { id: 'too_complex', icon: 'help-circle' },
  { id: 'other', icon: 'info' },
];

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const CONFIRM_PHRASE = t('deleteAccount.confirmPhrase');

  const handleClose = () => {
    if (!isDeleting) {
      setStep(1);
      setSelectedReason(null);
      setConfirmationText('');
      setError('');
      onClose();
    }
  };

  const handleReasonSelected = () => {
    setStep(2);
    setError('');
  };

  const handleWarningConfirm = () => {
    setStep(3);
    setError('');
    setTimeout(() => {
      inputRef.current?.focus();
      setTimeout(() => {
        contentRef.current?.scrollTo?.(0, contentRef.current?.scrollHeight ?? 0);
      }, 300);
    }, 100);
  };

  const handleBack = () => {
    if (step === 3) {
      setStep(2);
      setConfirmationText('');
    } else if (step === 2) {
      setStep(1);
    }
    setError('');
  };

  const handleFinalConfirm = async () => {
    if (confirmationText.trim() !== CONFIRM_PHRASE) {
      setError(t('deleteAccount.phraseMismatch'));
      return;
    }

    setIsDeleting(true);
    setError('');

    try {
      await onConfirm(selectedReason || undefined);
    } catch (err: unknown) {
      const e = err as { message?: string; response?: { data?: { details?: string; error?: string } } };
      console.error('❌ Ошибка удаления аккаунта:', err);

      let errorMessage = t('deleteAccount.deleteFailed');

      if (e?.message) {
        if (e.message.includes('Timeout') || e.message.includes('timeout')) {
          errorMessage = t('deleteAccount.timeoutError');
        } else if (e.message.includes('Network') || e.message.includes('Failed to fetch')) {
          errorMessage = t('deleteAccount.networkError');
        } else if (e.message.includes('Session expired') || e.message.includes('Unauthorized')) {
          errorMessage = t('deleteAccount.sessionExpired');
        } else if (e.response?.data?.details) {
          errorMessage = `${t('common.error')}: ${e.response.data.details}`;
        } else if (e.response?.data?.error) {
          errorMessage = `${t('common.error')}: ${e.response.data.error}`;
        }
      }

      setError(errorMessage);
      setIsDeleting(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1:
        return t('deleteAccount.whyLeaving');
      case 2:
        return t('deleteAccount.title');
      case 3:
        return t('deleteAccount.finalConfirmation');
      default:
        return '';
    }
  };

  // Сброс при открытии
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedReason(null);
      setConfirmationText('');
      setError('');
    }
  }, [isOpen]);

  // Шаг 1: Выбор причины
  const renderReasonStep = () => (
    <div className="delete-account-modal__step delete-account-modal__step--scroll">
      <div className="delete-account-modal__icon-circle delete-account-modal__icon-circle--primary">
        <Icon name="help-circle" size={48} color="var(--color-primary)" />
      </div>
      <h3 className="delete-account-modal__reason-title">{t('deleteAccount.helpUsImprove')}</h3>
      <p className="delete-account-modal__reason-subtitle">{t('deleteAccount.selectReason')}</p>

      <div className="delete-account-modal__reasons-list">
        {DELETION_REASONS.map((reason) => (
          <button
            key={reason.id}
            type="button"
            className={`delete-account-modal__reason-item ${selectedReason === reason.id ? 'delete-account-modal__reason-item--selected' : ''}`}
            onClick={() => {
              if (window.Telegram?.WebApp?.HapticFeedback) {
                window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
              }
              setSelectedReason(reason.id);
            }}
          >
            <span
              className={`delete-account-modal__reason-icon ${selectedReason === reason.id ? 'delete-account-modal__reason-icon--selected' : ''}`}
            >
              <Icon
                name={reason.icon}
                size={20}
                color={selectedReason === reason.id ? '#FFFFFF' : 'var(--color-text-secondary)'}
              />
            </span>
            <span className="delete-account-modal__reason-text">{t(`deleteAccount.reasons.${reason.id}`)}</span>
            {selectedReason === reason.id && (
              <Icon name="check-circle" size={20} color="var(--color-primary)" />
            )}
          </button>
        ))}
      </div>

      <div className="delete-account-modal__actions">
        <Button variant="secondary" onClick={handleClose} style={{ flex: 1 }}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleReasonSelected}
          style={{ flex: 1 }}
          disabled={!selectedReason}
        >
          {t('common.continue')}
        </Button>
      </div>
    </div>
  );

  // Шаг 2: Предупреждение
  const renderWarningStep = () => (
    <div className="delete-account-modal__step">
      <div className="delete-account-modal__icon-circle delete-account-modal__icon-circle--danger">
        <Icon name="alert-triangle" size={48} color="var(--color-danger, #FF3B30)" />
      </div>
      <h3 className="delete-account-modal__warning-title">{t('deleteAccount.areYouSure')}</h3>
      <p className="delete-account-modal__warning-text">{t('deleteAccount.warningText')}</p>

      <div className="delete-account-modal__delete-list">
        <div className="delete-account-modal__delete-list-title">{t('deleteAccount.willBeDeleted')}</div>
        {(['transactions', 'goals', 'accounts', 'settings'] as const).map((item) => (
          <div key={item} className="delete-account-modal__delete-list-item">
            <Icon name="x-circle" size={16} color="var(--color-danger, #FF3B30)" />
            <span>{t(`deleteAccount.deleteItems.${item}`)}</span>
          </div>
        ))}
      </div>

      <div className="delete-account-modal__actions">
        <Button variant="secondary" onClick={handleBack} style={{ flex: 1 }}>
          {t('common.back')}
        </Button>
        <Button
          variant="danger"
          onClick={handleWarningConfirm}
          style={{ flex: 1 }}
        >
          {t('deleteAccount.yesDelete')}
        </Button>
      </div>
    </div>
  );

  // Шаг 3: Финальное подтверждение
  const renderConfirmStep = () => (
    <div className="delete-account-modal__step delete-account-modal__step--scroll" ref={contentRef}>
      <div className="delete-account-modal__icon-circle delete-account-modal__icon-circle--danger delete-account-modal__icon-circle--sm">
        <Icon name="trash" size={36} color="var(--color-danger, #FF3B30)" />
      </div>
      <p className="delete-account-modal__confirm-text">{t('deleteAccount.enterPhraseToConfirm')}</p>

      <div className="delete-account-modal__phrase-box">
        <span className="delete-account-modal__phrase-text">&quot;{CONFIRM_PHRASE}&quot;</span>
      </div>

      <input
        ref={inputRef}
        type="text"
        className="delete-account-modal__input"
        placeholder={t('deleteAccount.enterPhrase')}
        value={confirmationText}
        onChange={(e) => {
          setConfirmationText(e.target.value);
          setError('');
        }}
        disabled={isDeleting}
        autoComplete="off"
        autoCapitalize="characters"
      />

      {error && <div className="delete-account-modal__error">{error}</div>}

      <div className="delete-account-modal__actions">
        <Button
          variant="secondary"
          onClick={handleBack}
          style={{ flex: 1 }}
          disabled={isDeleting}
        >
          {t('common.back')}
        </Button>
        <Button
          variant="danger"
          onClick={handleFinalConfirm}
          style={{ flex: 1 }}
          disabled={isDeleting || confirmationText.trim() === ''}
          loading={isDeleting}
        >
          {t('deleteAccount.deleteForever')}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={getStepTitle()}
      size="80vh"
      position="bottom"
      closeOnOverlayClick={!isDeleting}
      showCloseButton={!isDeleting}
    >
      <div className="delete-account-modal">
        {step === 1 && renderReasonStep()}
        {step === 2 && renderWarningStep()}
        {step === 3 && renderConfirmStep()}
      </div>
    </Modal>
  );
};
